import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Search,
  Plus,
  Users,
  MessageCircle,
  Bell,
  User,
  Trophy,
  UserPlus,
  CalendarDays
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import UserDropdown from '../common/UserDropdown';
import NotificationBell from '../common/NotificationBell';
import api from '../../services/api';

interface UserLayoutProps {
  children?: React.ReactNode;
}

const UserLayout: React.FC<UserLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [followRequestCount, setFollowRequestCount] = useState(0);

  // Load follow request count
  useEffect(() => {
    const loadFollowRequestCount = async () => {
      try {
        const response = await api.get('/users/follow-requests?limit=1');
        if (response.data.success) {
          setFollowRequestCount(response.data.data.pagination?.total || 0);
        }
      } catch (error) {
        console.error('Failed to load follow request count:', error);
      }
    };

    loadFollowRequestCount();
    // Refresh every 30 seconds
    const interval = setInterval(loadFollowRequestCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const openCreatePost = () => {
    // If already on home page, dispatch a custom event to open the modal
    if (location.pathname === '/home') {
      window.dispatchEvent(new CustomEvent('openCreatePost'));
    } else {
      // Navigate to home with query param to trigger the create post modal
      navigate('/home?createPost=true');
    }
  };

  const bottomNavItems = [
    { icon: Home, label: 'Home', path: '/home', key: 'home' },
    { icon: Search, label: 'Search', path: '/search', key: 'search' },
    { icon: Plus, label: 'Create', path: '#', key: 'create', action: openCreatePost },
    { icon: Users, label: 'Community', path: '/community', key: 'community' },
    { icon: CalendarDays, label: 'Events', path: '/tournaments', key: 'tournaments' },
    { icon: MessageCircle, label: 'Chats', path: '/chats', key: 'chats' }
  ];

  const sidebarItems = [
    { icon: Home, label: 'Home', path: '/home', key: 'home' },
    { icon: Search, label: 'Search', path: '/search', key: 'search' },
    { icon: Users, label: 'Community', path: '/community', key: 'community' },
    { icon: CalendarDays, label: 'Tournaments', path: '/tournaments', key: 'tournaments' },
    { icon: Trophy, label: 'Wall of Fame', path: '/wall-of-fame', key: 'wall-of-fame' },
    { icon: MessageCircle, label: 'Chats', path: '/chats', key: 'chats' },
    { icon: UserPlus, label: 'Follow Requests', path: '/follow-requests', key: 'follow-requests' },
    { icon: Bell, label: 'Notifications', path: '/notifications', key: 'notifications' },
    { icon: User, label: 'Profile', path: user ? `/profile/${user._id}` : '/profile', key: 'profile' }
  ];

  const isActivePath = (path: string) => {
    if (path === '#') return false;
    return location.pathname === path;
  };



  const handleNavClick = (item: any) => {
    if (item.action) {
      item.action();
    } else {
      navigate(item.path);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <img
            src="/app-icon.png"
            alt="Sportofolo"
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg"
          />
          <h1 className="text-base sm:text-lg font-bold text-gray-900">Sportofolo</h1>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <NotificationBell />
          <UserDropdown />
        </div>
      </header>

      {/* Main Content - offset for fixed header */}
      <main className="flex-1 pt-14 sm:pt-[52px] pb-16 md:pb-0 user-layout-main">
        {children || <Outlet />}
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-2 py-1.5 safe-area-bottom">
        <div className="flex justify-around">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <button
                key={item.key}
                onClick={() => handleNavClick(item)}
                className={`flex flex-col items-center justify-center space-y-0.5 p-1.5 rounded-lg transition-colors min-w-[48px] ${
                  isActive 
                    ? 'text-indigo-600' 
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                <div className="relative">
                  <Icon className="w-5 h-5" />
                  {isActive && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-indigo-600 rounded-full"></div>
                  )}
                </div>
                <span className="text-[10px] leading-tight">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Desktop Navigation (Sidebar) */}
      <nav className="hidden md:block fixed top-[52px] left-0 h-[calc(100vh-52px)] w-60 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="px-3 py-4">
          <div className="space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.path);
              
              return (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isActive 
                      ? 'bg-indigo-50 text-indigo-600' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </div>
                  {item.key === 'follow-requests' && followRequestCount > 0 && (
                    <span className="px-2 py-0.5 bg-indigo-500 text-white text-xs font-medium rounded-full">
                      {followRequestCount > 9 ? '9+' : followRequestCount}
                    </span>
                  )}
                </button>
              );
            })}
            <button
              onClick={openCreatePost}
              className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-left transition-colors bg-indigo-600 text-white hover:bg-indigo-700 mt-3"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium text-sm">Create Post</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Desktop Content Offset */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media (min-width: 768px) {
            .user-layout-main {
              margin-left: 15rem;
            }
          }
          .safe-area-bottom {
            padding-bottom: env(safe-area-inset-bottom, 0px);
          }
        `
      }} />
    </div>
  );
};

export default UserLayout;